import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  SakuraFlower,
  CherryBranchImg,
  JapaneseBadge,
} from "../components/SakuraDecorations";

export default function FaqPage() {
  const [selectedCategory, setSelectedCategory] = useState("alle");
  const [searchQuery, setSearchQuery] = useState("");
  const [openItems, setOpenItems] = useState({});

  const categories = [
    { id: "alle", label: "Alle spørgsmål", icon: "fa-solid fa-layer-group" },
    { id: "generelt", label: "Generelt om akupunktur", icon: "fa-solid fa-hand-holding-heart" },
    { id: "japansk", label: "Japansk akupunktur", icon: "fa-solid fa-feather-pointed" },
    { id: "oere", label: "Øre akupunktur", icon: "fa-solid fa-ear-listen" },
    { id: "vaegt", label: "Vægtregulering & Kost", icon: "fa-solid fa-apple-whole" },
    { id: "rygestop", label: "Rygestop behandling", icon: "fa-solid fa-ban-smoking" },
    { id: "tilskud", label: "Priser, Tilskud & Klinik", icon: "fa-solid fa-circle-info" },
  ];

  const allFaqs = useMemo(
    () => [
      // 1. Generelt om akupunktur
      {
        id: "gen-1",
        category: "generelt",
        categoryLabel: "Generelt om akupunktur",
        question: "Gør akupunktur ondt?",
        searchText: "Nej, akupunktur hos Kita Akupunktur er kendetegnet ved at være meget nænsom. Jeg anvender særligt tynde nåle helt ned til 0,12 mm i japansk akupunktur.",
        answer: (
          <>
            Nej, akupunktur hos Kita Akupunktur er kendetegnet ved at være meget nænsom. Jeg anvender særligt tynde nåle (helt ned til 0,12 mm i{" "}
            <Link to="/japansk-akupunktur" className="text-[#d74814] font-medium underline hover:text-[#593427]">
              japansk akupunktur
            </Link>
            ), og behandlingen tilpasses altid din personlige smertetærskel og sensitivitet. De fleste oplever det som en behagelig, afslappende stund.
          </>
        ),
      },
      {
        id: "gen-2",
        category: "generelt",
        categoryLabel: "Generelt om akupunktur",
        question: "Hvor mange behandlinger skal jeg have?",
        answer:
          "Antallet af behandlinger afhænger af, om der er tale om akutte eller mere kroniske symptomer. Mange mærker en positiv forandring allerede efter 3-5 behandlinger. Ved første konsultation gennemgår vi dine behov og lægger en individuel og realistisk behandlingsplan.",
      },
      {
        id: "gen-3",
        category: "generelt",
        categoryLabel: "Generelt om akupunktur",
        question: "Hvordan foregår en førstegangsbehandling?",
        answer:
          "Vi starter altid med en grundig indledende samtale om dine symptomer, din sygehistorie og dit generelle velbefindende. Herefter foretages en helhedsorienteret vurdering (herunder pulsdiagnose og evt. mavediagnose i japansk tradition), inden selve nålebehandlingen udføres i rolige og trygge rammer.",
      },
      {
        id: "gen-4",
        category: "generelt",
        categoryLabel: "Generelt om akupunktur",
        question: "Er der bivirkninger ved akupunktur?",
        answer:
          "Akupunktur er en meget sikker behandlingsform uden skadelige bivirkninger. Efter behandlingen kan du opleve en behagelig træthed, varme i kroppen eller forbigående let ømhed, hvilket er helt naturlige tegn på, at kroppens selvhelbredende processer er aktiveret. Sørg gerne for at drikke rigeligt med vand efterfølgende.",
      },

      // 2. Japansk akupunktur
      {
        id: "jap-1",
        category: "japansk",
        categoryLabel: "Japansk akupunktur",
        question: "Hvad er forskellen på klassisk og japansk akupunktur?",
        searchText: "Japansk akupunktur Keiraku Chiryo adskiller sig ved at benytte markant tyndere nåle.",
        answer: (
          <>
            <Link to="/japansk-akupunktur" className="text-[#d74814] font-medium underline hover:text-[#593427]">
              Japansk akupunktur (Keiraku Chiryo)
            </Link>{" "}
            adskiller sig fra{" "}
            <Link to="/akupunktur" className="text-[#d74814] font-medium underline hover:text-[#593427]">
              klassisk akupunktur
            </Link>{" "}
            ved at benytte markant tyndere nåle (0,12 mm mod traditionelt 0,25-0,30 mm), en meget overfladisk stikteknik og et stort fokus på palpation (puls- og mavediagnose). Behandlingen er blidere og særdeles velegnet til sensitive personer, ængstelige klienter og børn.
          </>
        ),
      },
      {
        id: "jap-2",
        category: "japansk",
        categoryLabel: "Japansk akupunktur",
        question: "Hvad er Keiraku Chiryo (Meridianterapi)?",
        answer:
          "Keiraku Chiryo er en traditionsrig japansk gren af akupunkturen, som fokuserer på meridianernes energiflow. Filosofien bygger på blid berøring og finjustering af kroppens vitale energier frem for kraftig stimulation, hvilket giver en dybdegående indre ro.",
      },
      {
        id: "jap-3",
        category: "japansk",
        categoryLabel: "Japansk akupunktur",
        question: "Hvad er kontaktnåle (Teishin)?",
        answer:
          "Kontaktnåle er specialudviklede japanske redskaber i ædelmetal, der udelukkende berører eller stryges blidt over hudens overflade uden at gennembryde huden. Dette gør behandlingen 100% smertefri og ideel til særligt sensitive klienter.",
      },
      {
        id: "jap-4",
        category: "japansk",
        categoryLabel: "Japansk akupunktur",
        question: "Hvor dybt stikkes nålene i huden?",
        answer:
          "I japansk akupunktur sættes nålene typisk kun 1-1,5 mm ind i det øverste hudlag – eller placeres udelukkende på overfladen som kontaktnåle. Dette står i kontrast til dybere stikmetoder og giver en behagelig og nænsom oplevelse.",
      },

      // 3. Øre akupunktur
      {
        id: "oere-1",
        category: "oere",
        categoryLabel: "Øre akupunktur",
        question: "Hvad er øre akupunktur, og hvordan virker det?",
        searchText: "Øre akupunktur auriculoterapi bygger på princippet om at hele kroppen afspejler sig i øret som et mikrosystem.",
        answer: (
          <>
            <Link to="/oere-akupunktur" className="text-[#d74814] font-medium underline hover:text-[#593427]">
              Øre akupunktur (auriculoterapi)
            </Link>{" "}
            bygger på princippet om, at hele kroppen afspejler sig i øret som et mikrosystem. Ved præcis stimulation af reflekspunkter i øret sendes beroligende og regulerende signaler til centralnervesystemet og kroppens organer.
          </>
        ),
      },
      {
        id: "oere-2",
        category: "oere",
        categoryLabel: "Øre akupunktur",
        question: "Hvad er forskellen på almindelige nåle og permanåle?",
        answer:
          "Almindelige nåle fjernes efter sessionen (typisk efter 20-30 minutters hvile på briksen), mens mikroskopiske permanåle eller magnetiske ørekugler fastgøres med et diskret lille hudfarvet plaster og kan sidde i øret i 3-7 dage for kontinuerlig virkning i hverdagen.",
      },
      {
        id: "oere-3",
        category: "oere",
        categoryLabel: "Øre akupunktur",
        question: "Kan øre akupunktur kombineres med kropsakupunktur?",
        answer:
          "Ja, det er en meget udbredt og effektiv kombination. Kropsakupunkturen afbalancerer hele organismen og nervesystemet, mens ørenålene giver målrettet stimulering mod f.eks. sukkertrang, uro, smerter eller nikotintrang.",
      },

      // 4. Vægtregulering & Kost
      {
        id: "vaegt-1",
        category: "vaegt",
        categoryLabel: "Vægtregulering & Kost",
        question: "Hvordan kan akupunktur støtte et vægttab?",
        searchText: "Akupunktur kan dæmpe overdreven appetit og sukkertrang, optimere fordøjelsessystemets funktion.",
        answer: (
          <>
            I vores forløb for{" "}
            <Link to="/vaegtregulering" className="text-[#d74814] font-medium underline hover:text-[#593427]">
              vægtregulering
            </Link>{" "}
            kan akupunktur og{" "}
            <Link to="/oere-akupunktur" className="text-[#d74814] font-medium underline hover:text-[#593427]">
              ørenåle
            </Link>{" "}
            dæmpe overdreven appetit og sukkertrang, optimere fordøjelsessystemets funktion, mindske stresshormoner (som ofte fremmer fedtdeponering) og styrke kroppens generelle energi og motivation.
          </>
        ),
      },
      {
        id: "vaegt-2",
        category: "vaegt",
        categoryLabel: "Vægtregulering & Kost",
        question: "Hvad indebærer den integrerede kostvejledning?",
        searchText: "Som certificeret kostvejleder fra At Work Skolen tilbyder jeg realistisk og individuel rådgivning.",
        answer: (
          <>
            Som certificeret kostvejleder fra At Work Skolen (læs mere under{" "}
            <Link to="/om-mig" className="text-[#d74814] font-medium underline hover:text-[#593427]">
              om mig
            </Link>
            ) tilbyder jeg realistisk og individuel rådgivning om nærende måltidssammensætning, stabilt blodsukker og holdbare hverdagsrutiner i vores{" "}
            <Link to="/vaegtregulering" className="text-[#d74814] font-medium underline hover:text-[#593427]">
              vægtreguleringsforløb
            </Link>{" "}
            – helt uden restriktive kure eller uoverkommelige forbud.
          </>
        ),
      },
      {
        id: "vaegt-3",
        category: "vaegt",
        categoryLabel: "Vægtregulering & Kost",
        question: "Hvor mange behandlinger anbefales til vægtregulering?",
        answer:
          "Et vægtreguleringsforløb tilrettelægges typisk over 4-8 uger med sessioner ugentligt eller hver 14. dag, kombineret med løbende opfølgning og justering af ørenåle mod sukkertrang.",
      },

      // 5. Rygestop behandling
      {
        id: "ryg-1",
        category: "rygestop",
        categoryLabel: "Rygestop behandling",
        question: "Hvor mange behandlinger skal der til for et rygestop?",
        searchText: "De fleste klienter har god gavn af 2-4 sessioner afhængigt af afhængighedsgrad.",
        answer: (
          <>
            De fleste klienter i et{" "}
            <Link to="/rygestop-behandling" className="text-[#d74814] font-medium underline hover:text-[#593427]">
              rygestop-forløb
            </Link>{" "}
            har god gavn af 2-4 sessioner afhængigt af afhængighedsgrad og tidligere stopforsøg. Første behandling placeres ideelt på selve din stopdag eller lige inden. Se priser på vores{" "}
            <Link to="/priser" className="text-[#d74814] font-medium underline hover:text-[#593427]">
              prisside
            </Link>
            .
          </>
        ),
      },
      {
        id: "ryg-2",
        category: "rygestop",
        categoryLabel: "Rygestop behandling",
        question: "Skal jeg stoppe med at ryge før første behandling?",
        answer:
          "Det anbefales, at du har fastsat en stopdato og møder op til første behandling uden at have røget samme dag, så akupunkturen og ørenålene straks kan støtte kroppen i at dæmpe abstinenser og nikotintrang.",
      },
      {
        id: "ryg-3",
        category: "rygestop",
        categoryLabel: "Rygestop behandling",
        question: "Hvordan hjælper ørenåle i hverdagen under et rygestop?",
        answer:
          "De små permanåle eller magnetkugler i øret stimulerer reflekspunkter, som dæmper uro, rastløshed og rygetrang. Hvis trangen melder sig akut i hverdagen, kan du aktivere punktet ved et let tryk på øreplastret.",
      },

      // 6. Priser, Tilskud & Praktisk
      {
        id: "til-1",
        category: "tilskud",
        categoryLabel: "Priser, Tilskud & Klinik",
        question: "Kan jeg få tilskud fra Sygeforsikring “danmark”?",
        searchText: "Ja som RAB-registreret akupunktør opfylder jeg alle krav så du kan modtage tilskud til akupunktur fra Sygeforsikring danmark.",
        answer: (
          <>
            Ja, som{" "}
            <Link to="/om-mig" className="text-[#d74814] font-medium underline hover:text-[#593427]">
              RAB-registreret akupunktør
            </Link>{" "}
            og medlem af Praktiserende Akupunktører (PA) opfylder jeg alle krav, så du kan modtage tilskud til akupunktur fra Sygeforsikring “danmark” (forudsat der foreligger en lægelig diagnose). Se mere under{" "}
            <Link to="/priser" className="text-[#d74814] font-medium underline hover:text-[#593427]">
              priser og tilskud
            </Link>
            .
          </>
        ),
      },
      {
        id: "til-2",
        category: "tilskud",
        categoryLabel: "Priser, Tilskud & Klinik",
        question: "Dækker private sundhedsforsikringer behandlingen?",
        answer:
          "Mange private sundhedsforsikringer og arbejdsgiverbetalte forsikringer (f.eks. Danica, Topdanmark, Tryg, Skandia, PFA m.fl.) dækker akupunktur udført af en RAB-registreret behandler. Kontakt dit forsikringsselskab for at få en bevilling.",
      },
      {
        id: "til-3",
        category: "tilskud",
        categoryLabel: "Priser, Tilskud & Klinik",
        question: "Hvor ligger klinikken, og er der gode parkeringsmuligheder?",
        answer:
          "Klinikken er beliggende på Kantorparken 11 i 8240 Risskov, få minutters kørsel fra Aarhus N, Egå og Lystrup. Der er gratis og ugeneret parkering lige ude foran huset.",
      },
      {
        id: "til-4",
        category: "tilskud",
        categoryLabel: "Priser, Tilskud & Klinik",
        question: "Hvordan bestiller jeg tid til behandling?",
        searchText: "Du er altid meget velkommen til at ringe direkte eller benytte kontaktformularen her på hjemmesiden.",
        answer: (
          <>
            Du er altid meget velkommen til at ringe direkte til mig på (+45) 29 38 71 86, sende en mail til kontakt@kita-akupunktur.dk eller benytte formularen på vores{" "}
            <Link to="/kontakt" className="text-[#d74814] font-medium underline hover:text-[#593427]">
              kontaktside
            </Link>
            .
          </>
        ),
      },
      {
        id: "til-5",
        category: "tilskud",
        categoryLabel: "Priser, Tilskud & Klinik",
        question: "Hvilke betalingsformer modtages i klinikken?",
        answer:
          "Der kan nemt og bekvemt afregnes efter hver behandling via MobilePay, straksoverførsel eller kontant.",
      },
    ],
    []
  );

  // Filter FAQs based on selected category and search query
  const filteredFaqs = useMemo(() => {
    return allFaqs.filter((item) => {
      const matchesCategory =
        selectedCategory === "alle" || item.category === selectedCategory;
      const answerContent =
        typeof item.answer === "string"
          ? item.answer
          : item.searchText || "";

      const matchesQuery =
        searchQuery.trim() === "" ||
        item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        answerContent.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.categoryLabel.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesCategory && matchesQuery;
    });
  }, [allFaqs, selectedCategory, searchQuery]);

  const toggleItem = (id) => {
    setOpenItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const expandAll = () => {
    const nextState = {};
    filteredFaqs.forEach((item) => {
      nextState[item.id] = true;
    });
    setOpenItems(nextState);
  };

  const collapseAll = () => {
    setOpenItems({});
  };

  // Schema.org FAQPage structured data
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: allFaqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: typeof faq.answer === "string" ? faq.answer : faq.searchText || faq.question,
      },
    })),
  };

  return (
    <>
      <Helmet>
        <title>FAQ om Akupunktur | Spørgsmål & Svar</title>
        <meta
          name="description"
          content="Find svar på ofte stillede spørgsmål om akupunkturbehandling, nåle, priser og tilskud hos Kita Akupunktur i Risskov. Få overblik og klar besked her."
        />
        <meta property="og:title" content="FAQ om Akupunktur | Spørgsmål & Svar" />
        <meta
          property="og:description"
          content="Find svar på ofte stillede spørgsmål om akupunkturbehandling, nåle, priser og tilskud hos Kita Akupunktur i Risskov. Få overblik og klar besked her."
        />
        <link rel="canonical" href="https://kita-akupunktur.dk/faq/" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Hero Header */}
      <section className="relative bg-gradient-to-b from-[#FFF5EE] via-[#FFFAF7] to-[#FFF5EE] pt-14 pb-16 lg:pt-20 lg:pb-20 border-b border-[#ffe0cf]/80 overflow-hidden">
        <div className="absolute top-0 right-0 w-80 opacity-15 pointer-events-none">
          <CherryBranchImg index={1} className="w-full h-auto" />
        </div>
        <div className="absolute bottom-0 left-0 w-64 opacity-15 pointer-events-none">
          <CherryBranchImg index={3} className="w-full h-auto" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <div className="inline-flex items-center space-x-2 px-3.5 py-1.5 rounded-full bg-[#ffe0cf]/50 border border-[#ffe0cf] text-[#d74814] text-xs font-semibold uppercase tracking-wider mb-4">
            <SakuraFlower className="w-3.5 h-3.5" />
            <span>Spørgsmål & Svar</span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#111827] max-w-3xl mx-auto leading-tight mb-4">
            Ofte stillede spørgsmål
          </h1>
          <p className="text-base sm:text-lg text-[#1f2937] max-w-2xl mx-auto leading-relaxed">
            Find hurtigt og nemt svar på alt om behandlingsformer, nænsomme nåleteknikker, tilskud fra Sygeforsikring “danmark” og praktisk information om klinikken hos{" "}
            <Link to="/" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b3360b] hover:decoration-[#d74814] transition-colors">
              Kita Akupunktur
            </Link>{" "}
            i Risskov.
          </p>

          {/* Search Bar */}
          <div className="mt-8 max-w-xl mx-auto relative">
            <div className="relative">
              <i className="fa-solid fa-magnifying-glass absolute left-4 top-1/2 -translate-y-1/2 text-[#d74814] text-sm pointer-events-none" aria-hidden="true" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Søg efter spørgsmål eller emne (f.eks. 'gør det ondt', 'tilskud', 'rygestop')..."
                className="w-full pl-11 pr-10 py-3.5 bg-white/95 backdrop-blur-md rounded-2xl border border-[#ffe0cf] text-sm text-[#111827] placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-[#d74814]/30 focus:border-[#d74814] shadow-xs transition-all"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-[#d74814] p-1 text-sm transition-colors"
                  aria-label="Ryd søgning"
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 space-y-10">
        {/* Category Filters */}
        {/* Mobile & Tablet Dropdown (until 1024px) */}
        <div className="lg:hidden w-full max-w-md mx-auto">
          <label htmlFor="faq-category-select" className="block text-xs font-semibold text-[#593427] uppercase tracking-wider mb-2 text-center">
            Vælg emne eller kategori
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-[#d74814]">
              <i className={categories.find((c) => c.id === selectedCategory)?.icon || "fa-solid fa-list-check"} aria-hidden="true" />
            </div>
            <select
              id="faq-category-select"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full pl-10 pr-10 py-3 rounded-2xl bg-white/95 border border-[#ffe0cf] text-[#111827] font-semibold text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-[#d74814]/30 focus:border-[#d74814] appearance-none cursor-pointer"
            >
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id} className="text-[#111827] py-1 font-medium">
                  {cat.label}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 pr-3.5 flex items-center pointer-events-none text-[#593427]">
              <i className="fa-solid fa-chevron-down text-xs" aria-hidden="true" />
            </div>
          </div>
        </div>

        {/* Desktop Category Pills (from 1024px / lg:flex) */}
        <div className="hidden lg:flex flex-wrap items-center justify-center gap-2 sm:gap-2.5 pb-2">
          {categories.map((cat) => {
            const isSelected = selectedCategory === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => setSelectedCategory(cat.id)}
                className={`inline-flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-medium transition-all cursor-pointer border ${
                  isSelected
                    ? "bg-[#d74814] text-white border-[#d74814] shadow-xs"
                    : "bg-white/85 backdrop-blur-md text-[#111827] border-[#ffe0cf] hover:bg-[#fff5ee] hover:text-[#d74814]"
                }`}
              >
                <i className={`${cat.icon} text-xs ${isSelected ? "text-white" : "text-[#d74814]"}`} aria-hidden="true" />
                <span>{cat.label}</span>
              </button>
            );
          })}
        </div>

        {/* Results Header & Expand/Collapse controls */}
        <div className="flex items-center justify-between border-b border-[#ffe0cf]/80 pb-4 text-xs sm:text-sm text-[#1f2937]">
          <div>
            Viser <span className="font-bold text-[#111827]">{filteredFaqs.length}</span> {filteredFaqs.length === 1 ? "spørgsmål" : "spørgsmål"}
            {selectedCategory !== "alle" && (
              <span className="ml-1 text-stone-500">
                i kategorien <strong className="text-[#111827]">{categories.find((c) => c.id === selectedCategory)?.label}</strong>
              </span>
            )}
            {searchQuery && (
              <span className="ml-1 text-stone-500">
                matchende "<strong className="text-[#d74814]">{searchQuery}</strong>"
              </span>
            )}
          </div>

          <div className="flex items-center space-x-2 shrink-0">
            <button
              type="button"
              onClick={expandAll}
              className="text-xs text-[#593427] hover:text-[#d74814] font-medium px-2.5 py-1 rounded-lg hover:bg-stone-100 transition-colors"
            >
              Åbn alle
            </button>
            <span className="text-stone-300">|</span>
            <button
              type="button"
              onClick={collapseAll}
              className="text-xs text-[#593427] hover:text-[#d74814] font-medium px-2.5 py-1 rounded-lg hover:bg-stone-100 transition-colors"
            >
              Luk alle
            </button>
          </div>
        </div>

        {/* FAQ Accordion List */}
        {filteredFaqs.length === 0 ? (
          <div className="text-center py-16 bg-white/70 rounded-3xl border border-[#ffe0cf] p-8 space-y-4">
            <div className="w-12 h-12 rounded-full bg-[#ffe0cf]/60 text-[#d74814] flex items-center justify-center mx-auto text-lg">
              <i className="fa-solid fa-magnifying-glass" />
            </div>
            <h3 className="text-lg font-serif text-[#111827]">Ingen spørgsmål matchede din søgning</h3>
            <p className="text-sm text-[#1f2937] max-w-md mx-auto">
              Prøv at søge med et andet ord eller vælg en anden kategori ovenfor. Du er også altid velkommen til at kontakte klinikken direkte.
            </p>
            <button
              type="button"
              onClick={() => {
                setSearchQuery("");
                setSelectedCategory("alle");
              }}
              className="inline-flex items-center px-4 py-2 rounded-xl bg-[#d74814] text-white text-xs font-semibold hover:bg-[#b83a0d] transition-colors"
            >
              Nulstil søgning & filter
            </button>
          </div>
        ) : (
          <div className="space-y-3.5">
            {filteredFaqs.map((faq) => {
              const isOpen = !!openItems[faq.id];

              return (
                <div
                  key={faq.id}
                  className="bg-white/90 backdrop-blur-md rounded-2xl border border-[#ffe0cf] overflow-hidden shadow-2xs transition-all hover:border-[#d74814]/40"
                >
                  <button
                    type="button"
                    onClick={() => toggleItem(faq.id)}
                    aria-expanded={isOpen}
                    className="w-full p-5 sm:p-6 text-left flex items-start justify-between gap-4 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#d74814]/30"
                  >
                    <div className="pr-2">
                      <h2 className="text-base sm:text-lg font-serif font-bold text-[#111827] leading-snug group-hover:text-[#d74814] transition-colors">
                        {faq.question}
                      </h2>
                    </div>

                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 border transition-all duration-300 mt-1 ${
                        isOpen
                          ? "bg-[#d74814] text-white border-[#d74814] rotate-180"
                          : "bg-stone-50 text-[#111827] border-stone-200"
                      }`}
                    >
                      <i className="fa-solid fa-chevron-down text-xs" aria-hidden="true" />
                    </div>
                  </button>

                  <div
                    className={`grid transition-all duration-300 ease-in-out ${
                      isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="px-5 pb-5 sm:px-6 sm:pb-6 pt-1 text-sm sm:text-base text-[#111827] font-medium leading-relaxed border-t border-[#ffe0cf]/60">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Bottom Help & Contact CTA */}
        <section className="bg-gradient-to-br from-[#3D2118] to-[#593427] text-white p-8 sm:p-10 lg:p-12 rounded-3xl relative overflow-hidden shadow-lg mt-14">
          <div className="absolute top-0 right-0 w-72 opacity-20 pointer-events-none">
            <CherryBranchImg index={5} className="w-full h-auto" />
          </div>

          <div className="max-w-3xl relative z-10 space-y-4">
            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-white/10 text-[#ffe0cf] text-xs font-semibold">
              <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
              <span>Personlig vejledning</span>
            </div>

            <h2 className="text-2xl sm:text-3xl font-serif text-white font-normal">
              Fandt du ikke svar på dit spørgsmål?
            </h2>

            <p className="text-sm sm:text-base text-[#ffe0cf]/90 leading-relaxed">
              Du er altid hjertelig velkommen til at kontakte mig for en uforpligtende samtale om dine symptomer, eller hvis du har særlige spørgsmål til et behandlingsforløb.
            </p>

            <div className="pt-3 flex flex-wrap items-center gap-3 sm:gap-4">
              <a
                href="tel:29387186"
                className="inline-flex items-center px-5 py-3 rounded-full bg-[#d74814] text-white text-sm font-semibold hover:bg-[#b83a0d] transition-all shadow-sm"
              >
                <i className="fa-solid fa-phone mr-2" aria-hidden="true" />
                <span>Ring (+45) 29 38 71 86</span>
              </a>

              <Link
                to="/kontakt"
                className="inline-flex items-center px-5 py-3 rounded-full bg-white/15 text-white hover:bg-white/25 border border-white/30 text-sm font-semibold transition-all"
              >
                <i className="fa-solid fa-envelope mr-2" aria-hidden="true" />
                <span>Send en besked</span>
              </Link>

              <Link
                to="/priser"
                className="inline-flex items-center px-5 py-3 rounded-full bg-white/15 text-white hover:bg-white/25 border border-white/30 text-sm font-semibold transition-all"
              >
                <i className="fa-solid fa-tag mr-2" aria-hidden="true" />
                <span>Se priser & tilskud</span>
              </Link>

              <Link
                to="/om-mig"
                className="inline-flex items-center px-5 py-3 rounded-full bg-white/15 text-white hover:bg-white/25 border border-white/30 text-sm font-semibold transition-all"
              >
                <i className="fa-solid fa-user-doctor mr-2" aria-hidden="true" />
                <span>Om Mayumi Kita Thiele</span>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
